import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { db } from '../lib/firebase';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import QRCode from 'react-qr-code';
import { AttendanceComp } from '../components/events/AttendanceComp';
import { MatchList } from '../components/events/MatchList';
import { StartggImportModal } from '../components/events/StartggImportModal';
import { CollectionPaths } from 'shared_schemas';

interface EventData {
    id: string;
    title: string;
    date: string;
    type: string;
    games: string[];
    location: string;
    startTime: string;
    endTime: string;
    coach?: string;
    status?: 'scheduled' | 'active' | 'completed';
    startggId?: string;
    startggUrl?: string;
    startggSlug?: string;
    liveHeadline?: string;
    liveScore?: string;
    liveHostCredit?: string;
}

const TABS = ['overview', 'matches', 'attendance', 'checkins', 'startgg'] as const;
type TabType = typeof TABS[number];

export default function AdminEventDetailPage() {
    const { eventId } = useParams<{ eventId: string }>();
    const { user, userDoc, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    const [event, setEvent] = useState<EventData | null>(null);
    const [activeTab, setActiveTab] = useState<TabType>('overview');
    const [loading, setLoading] = useState(true);
    const [showQR, setShowQR] = useState(false);

    // Live update state
    const [liveHeadline, setLiveHeadline] = useState('');
    const [liveScore, setLiveScore] = useState('');
    const [liveHostCredit, setLiveHostCredit] = useState('');

    // Check admin access
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    useEffect(() => {
        if (!authLoading && (!user || (userDoc?.role !== 'admin' && userDoc?.role !== 'coach'))) {
            navigate('/login');
        }
    }, [user, userDoc, authLoading, navigate]);

    // Load data
    useEffect(() => {
        async function loadData() {
            if (!eventId) return;

            try {
                const eventDoc = await getDoc(doc(db, CollectionPaths.events, eventId));
                if (!eventDoc.exists()) {
                    navigate('/app/admin/events');
                    return;
                }
                const data = eventDoc.data() as EventData;
                setEvent({ ...data, id: eventDoc.id });

                // Init live fields
                if (data.liveHeadline) setLiveHeadline(data.liveHeadline);
                if (data.liveScore) setLiveScore(data.liveScore);
                if (data.liveHostCredit) setLiveHostCredit(data.liveHostCredit);
            } catch (err) {
                console.error('Error loading data:', err);
            } finally {
                setLoading(false);
            }
        }
        loadData();
    }, [eventId, navigate]);

    const handleStatusUpdate = async (newStatus: 'scheduled' | 'active' | 'completed') => {
        if (!eventId) return;
        try {
            await updateDoc(doc(db, CollectionPaths.events, eventId), {
                status: newStatus,
                updatedAt: Timestamp.now()
            });
            setEvent(prev => prev ? { ...prev, status: newStatus } : null);
        } catch (err) {
            console.error('Failed to update status', err);
        }
    };

    const handleLiveUpdate = async () => {
        if (!eventId) return;
        try {
            await updateDoc(doc(db, CollectionPaths.events, eventId), {
                liveHeadline: liveHeadline.trim() || null,
                liveScore: liveScore.trim() || null,
                liveHostCredit: liveHostCredit.trim() || null,
                updatedAt: Timestamp.now()
            });
            // Update local state event wrapper if needed, though we track fields separately
            alert('Live info updated!');
        } catch (err) {
            console.error('Failed to update live info', err);
            alert('Error updating live info');
        }
    };

    const checkinUrl = `${window.location.origin}/checkin/${eventId}`;

    if (loading || authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!event) return <div className="p-8 text-center text-secondary-400">Event not found</div>;

    // Full-screen QR
    if (showQR) {
        return (
            <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 cursor-pointer" onClick={() => setShowQR(false)}>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">{event.title}</h1>
                <p className="text-xl text-gray-600 mb-8">{event.date} • Scan to Check In</p>
                <div className="bg-white p-4 rounded-xl shadow-2xl">
                    <QRCode value={checkinUrl} size={400} />
                </div>
                <p className="mt-8 text-gray-500">Click anywhere to close</p>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex items-center gap-4">
                    <Link to="/app/admin/events" className="btn-secondary px-3 py-2">
                        ← Back
                    </Link>
                    <div>
                        <h1 className="text-2xl font-display font-bold text-white">{event.title}</h1>
                        <p className="text-secondary-400">{event.date} • {event.location}</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    {event.status !== 'active' && (
                        <button onClick={() => handleStatusUpdate('active')} className="btn-primary">
                            ▶ Activate Event
                        </button>
                    )}
                    {event.status === 'active' && (
                        <div className="flex gap-2">
                            <div className="bg-red-500/20 text-red-400 px-3 py-2 rounded-lg border border-red-500/30 flex items-center animate-pulse font-bold text-sm uppercase">
                                ● Live
                            </div>
                            <button onClick={() => handleStatusUpdate('completed')} className="btn-secondary border-green-500 text-green-400">
                                ✓ Complete Event
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-secondary-700">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    {TABS.map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`${activeTab === tab
                                ? 'border-primary-500 text-primary-500'
                                : 'border-transparent text-secondary-400 hover:text-white hover:border-secondary-300'
                                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize transition-colors`}
                        >
                            {tab === 'startgg' ? 'Start.gg' : tab}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Content include */}
            <div className="min-h-[400px]">
                {activeTab === 'overview' && (
                    <div className="grid lg:grid-cols-2 gap-8">
                        <div className="space-y-6">
                            <div className="card space-y-4">
                                <h3 className="text-lg font-bold text-white">Event Details</h3>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div className="text-secondary-400">Time</div>
                                    <div className="text-white text-right">{event.startTime} - {event.endTime}</div>
                                    <div className="text-secondary-400">Games</div>
                                    <div className="text-white text-right space-x-1">
                                        {event.games.map(g => <span key={g} className="badge bg-secondary-900">{g}</span>)}
                                    </div>
                                    <div className="text-secondary-400">Coach</div>
                                    <div className="text-white text-right">{event.coach || 'Unassigned'}</div>
                                    <div className="text-secondary-400">Status</div>
                                    <div className="text-white text-right uppercase tracking-wide font-bold">{event.status || 'Scheduled'}</div>
                                </div>
                            </div>
                            {event.startggUrl && (
                                <div className="card bg-blue-500/10 border-blue-500/20">
                                    <h3 className="text-lg font-bold text-blue-400">Linked to Start.gg</h3>
                                    <p className="text-sm text-secondary-400 mt-1">This event is imported from Start.gg.</p>
                                    <a href={event.startggUrl} target="_blank" rel="noreferrer" className="btn-secondary mt-4 block text-center">
                                        View on Start.gg
                                    </a>
                                </div>
                            )}
                        </div>

                        {/* Live Event Control */}
                        {event.status === 'active' && (
                            <div className="card border-red-500/30 bg-red-900/10 space-y-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                                    <h3 className="text-lg font-bold text-white">Live Event Control</h3>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-secondary-300 mb-1">
                                        Live Headline
                                    </label>
                                    <input
                                        type="text"
                                        value={liveHeadline}
                                        onChange={(e) => setLiveHeadline(e.target.value)}
                                        placeholder="e.g. Grand Finals vs Davison"
                                        className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:border-red-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-secondary-300 mb-1">
                                        Current Score / Status
                                    </label>
                                    <input
                                        type="text"
                                        value={liveScore}
                                        onChange={(e) => setLiveScore(e.target.value)}
                                        placeholder="e.g. 2 - 1"
                                        className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:border-red-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-secondary-300 mb-1">
                                        Host Credit / Special Thanks
                                    </label>
                                    <input
                                        type="text"
                                        value={liveHostCredit}
                                        onChange={(e) => setLiveHostCredit(e.target.value)}
                                        placeholder="e.g. Hosted by Kettering University"
                                        className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:border-red-500"
                                    />
                                </div>

                                <button
                                    onClick={handleLiveUpdate}
                                    className="w-full py-2 bg-red-600 hover:bg-red-500 text-white font-bold rounded-lg transition-colors"
                                >
                                    Update Live Banner
                                </button>
                            </div>
                        )}

                        <div className="card flex flex-col items-center text-center space-y-4">
                            <h3 className="font-bold text-white">Quick Check-in</h3>
                            <QRCode value={checkinUrl} size={150} />
                            <button onClick={() => setShowQR(true)} className="btn-secondary w-full">
                                Show Full Screen QR
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'attendance' && (
                    <AttendanceComp
                        schoolId={userDoc?.schoolId || 'peck-school'} // Fallback or dynamic
                        eventId={eventId!}
                        eventDate={event.date}
                    />
                )}

                {activeTab === 'checkins' && (
                    <div className="card">
                        <p className="text-secondary-400 text-center py-8">
                            (Legacy Check-in List - To be merged with Attendance)
                        </p>
                    </div>
                )}

                {activeTab === 'matches' && (
                    <MatchList
                        eventId={eventId!}
                        startggEventId={event.startggId}
                        isAdmin={true}
                    />
                )}

                {activeTab === 'startgg' && (
                    <div className="card space-y-6">
                        <h3 className="text-xl font-bold text-white">Start.gg Integration</h3>
                        {event.startggId ? (
                            <div className="space-y-4">
                                <div className="p-4 bg-secondary-900/50 rounded-lg border border-secondary-700">
                                    <div className="flex justify-between items-center">
                                        <span className="text-secondary-300">Tournament Slug</span>
                                        <span className="font-mono text-white">{event.startggSlug}</span>
                                    </div>
                                    <div className="flex justify-between items-center mt-2">
                                        <span className="text-secondary-300">Event ID</span>
                                        <span className="font-mono text-white">{event.startggId}</span>
                                    </div>
                                </div>
                                <button className="btn-primary w-full sm:w-auto">
                                    Sync Scores & Results (Coming Soon)
                                </button>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-secondary-400">
                                <p>This event is not linked to Start.gg.</p>
                                <p className="text-sm mt-2 mb-4">Link an event to enable syncing.</p>
                                <button
                                    onClick={() => setLinkModalOpen(true)}
                                    className="btn-secondary"
                                >
                                    🔗 Link to Start.gg
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <StartggImportModal
                open={linkModalOpen}
                onClose={() => setLinkModalOpen(false)}
                onSuccess={() => {
                    // Refresh data
                    navigate(0);
                }}
                mode="link"
                existingEventId={eventId}
            />
        </div>
    );
}
